import React from "react";
import { WestyMark } from "../ui";
import { SettingsDrawer } from "./SettingsDrawer";
import { ContentHeader, SyncNotice, SyncWarning } from "./ContentHeader";
import { ICONS, buildNav, NavData, NavPersona } from "@/lib/nav";
import { useUI } from "@/lib/theme";
import type { SidebarSection, SidebarSub } from "./Sidebar";
import type { AppShellProps } from "./AppShell";
import { HealthView } from "../tabs/Salud";

/** Icono SVG stroke para el bottom-nav. */
function NavIcon({ d, active, accentRgb }: { d: string; active: boolean; accentRgb: string }) {
  return (
    <svg
      viewBox="0 0 24 24"
      className="h-6 w-6"
      fill="none"
      strokeWidth="1.75"
      strokeLinecap="round"
      strokeLinejoin="round"
      style={{ stroke: active ? `rgb(${accentRgb})` : "currentColor" }}
      aria-hidden
    >
      <path d={d} />
    </svg>
  );
}

type MobileShellInternalProps = Omit<AppShellProps, "children"> & {
  section: SidebarSection;
  sub: SidebarSub;
  rangeDays: number | null;
  settingsOpen: boolean;
  onToggleSettings: () => void;
  onNavigate: (section: SidebarSection, sub: SidebarSub) => void;
  onRangeChange: (v: number | null) => void;
  children: AppShellProps["children"];
};

export function MobileShell({
  persona,
  navData,
  name,
  role,
  avatar,
  onLogout,
  onSync,
  syncing,
  syncNotice,
  lastSyncedAt,
  isAdmin,
  devImpersonating,
  devDemoAthletes,
  onReload,
  readOnly,
  section,
  sub,
  rangeDays,
  settingsOpen,
  onToggleSettings,
  onNavigate,
  onRangeChange,
  children,
}: MobileShellInternalProps) {
  const { t } = useUI();
  const groups = buildNav(persona, navData);

  // Secciones principales para el bottom-nav
  const bottomItems = [
    { key: "entrenamiento" as SidebarSection, icon: ICONS["entrenamiento"] ?? "", labelKey: "nav_entrenamiento", accent: "252 76 2" },
    { key: "salud" as SidebarSection, icon: ICONS["salud"] ?? "", labelKey: "seg_salud", accent: "20 184 166" },
    { key: "calendario" as SidebarSection, icon: ICONS["calendario"] ?? "", labelKey: "tab_calendario", accent: "234 179 8" },
    { key: "coach" as SidebarSection, icon: ICONS["coach"] ?? "", labelKey: "seg_coach", accent: "139 92 246" },
    { key: "red" as SidebarSection, icon: ICONS["red"] ?? "", labelKey: persona === "athlete" ? "nav_red_athlete" : "nav_red_coach_zone", accent: "79 110 247", badge: navData.unreadCount },
  ];

  // Sub-items de la sección activa
  const activeGroup = groups.flatMap((g) => g.items).find((it) => it.key === section);
  const activeSubs = activeGroup?.subs ?? [];

  // Lógica de content header igual que desktop
  const showRange =
    (section === "entrenamiento" || section === "salud") &&
    sub !== "material";

  const { breadcrumb, title } = (() => {
    if (section === "coach") return { title: t("seg_coach") };
    if (section === "calendario") return { breadcrumb: t("nav_group_principal"), title: t("tab_calendario") };
    if (section === "red") {
      const map: Record<string, string> = {
        atletas: "coach_subtab_athletes",
        mensajes: "nav_red_messages",
        codigos: "nav_red_codes",
        entrenador: "nav_red_entrenador",
        cal_coach: "tab_calendario",
      };
      return { breadcrumb: t("nav_group_red"), title: t(map[sub] || "nav_red_athlete") };
    }
    if (section === "salud") return { breadcrumb: t("seg_salud"), title: t("tab_" + (sub || "s_resumen")) };
    const subLabel = sub ? (sub === "resumen" ? t("tab_resumen") : t(`tab_${sub}`) !== `tab_${sub}` ? t(`tab_${sub}`) : t(`cat_${sub}`)) : t("tab_resumen");
    return { breadcrumb: t("nav_entrenamiento"), title: subLabel };
  })();

  const healthTab: HealthView = (["s_resumen", "s_cuerpo", "s_sueno", "s_cruce"].includes(sub) ? sub : "s_resumen") as HealthView;
  const fitnessTab = section === "entrenamiento" ? (sub || "resumen") : "resumen";

  return (
    <div className="flex h-[100dvh] flex-col overflow-hidden">
      {/* Top content header (sticky dentro del flex column) */}
      <ContentHeader
        title={title}
        breadcrumb={breadcrumb}
        showRange={showRange}
        rangeDays={rangeDays}
        onRangeChange={onRangeChange}
        syncing={syncing}
        onSync={onSync}
        readOnly={readOnly}
      />

      {/* Sub-tabs horizontales scrollables */}
      {activeSubs.length > 0 && (
        <div className="flex gap-1 overflow-x-auto border-b border-line bg-panel/80 px-3 py-2 backdrop-blur">
          {activeSubs.map((s) => {
            const isActive = sub === s.key;
            return (
              <button
                key={s.key}
                onClick={() => onNavigate(section, s.key)}
                aria-current={isActive ? "page" : undefined}
                style={isActive ? { color: `rgb(${s.accent})`, backgroundColor: `rgb(${s.accent} / 0.1)` } : undefined}
                className={`whitespace-nowrap rounded-lg px-3 py-1.5 text-[13px] font-semibold transition ${
                  isActive ? "" : "text-muted hover:text-fg"
                }`}
              >
                {t(s.labelKey)}
              </button>
            );
          })}
        </div>
      )}

      {/* Banners */}
      <SyncWarning lastSyncedAt={lastSyncedAt} />
      {syncNotice && <SyncNotice type={syncNotice} />}

      {/* Contenido scrollable */}
      <main className="flex-1 overflow-y-auto px-4 py-4 pb-24">
        {children({
          section,
          sub,
          rangeDays,
          healthTab,
          fitnessTab,
          onSubChange: (newSub) => onNavigate(section, newSub),
          onSectionChange: (newSection, newSub = "") => onNavigate(newSection, newSub),
          openSettings: onToggleSettings,
        })}
      </main>

      {/* Bottom navigation */}
      <nav className="fixed bottom-0 left-0 right-0 z-30 flex border-t border-line bg-panel/95 backdrop-blur safe-area-inset-bottom">
        {bottomItems.map((item) => {
          const isActive = section === item.key;
          const badge = item.badge ?? 0;
          return (
            <button
              key={item.key}
              onClick={() => {
                const navGroup = groups.flatMap((g) => g.items).find((it) => it.key === item.key);
                const defaultSub = navGroup?.subs?.[0]?.key ?? "";
                onNavigate(item.key, isActive ? sub : defaultSub);
              }}
              aria-current={isActive ? "page" : undefined}
              className="relative flex flex-1 flex-col items-center gap-0.5 py-2.5 text-[10.5px] font-semibold transition"
              style={{ color: isActive ? `rgb(${item.accent})` : undefined }}
            >
              <span className="relative">
                <NavIcon d={item.icon} active={isActive} accentRgb={item.accent} />
                {badge > 0 && (
                  <span className="absolute -right-1.5 -top-1.5 flex h-4 w-4 items-center justify-center rounded-full bg-accent text-[9px] font-bold text-white">
                    {badge > 9 ? "9+" : badge}
                  </span>
                )}
              </span>
              <span className={isActive ? "" : "text-muted"}>{t(item.labelKey)}</span>
            </button>
          );
        })}
        {/* Settings en bottom nav */}
        <button
          onClick={onToggleSettings}
          className="flex flex-1 flex-col items-center gap-0.5 py-2.5 text-[10.5px] font-semibold text-muted transition"
        >
          <NavIcon d={ICONS["ajustes"] ?? ""} active={false} accentRgb="123 118 107" />
          <span>{t("nav_settings")}</span>
        </button>
      </nav>

      {/* Settings drawer */}
      <SettingsDrawer
        open={settingsOpen}
        onClose={onToggleSettings}
        onLogout={onLogout}
        onReload={onReload}
        isAdmin={isAdmin}
        devImpersonating={devImpersonating}
        devDemoAthletes={devDemoAthletes}
      />
    </div>
  );
}
